import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getMyTabs } from '../api/tabPermissionApi';

const TabPermissionContext = createContext(null);

const CACHE_KEY = 'tab_permissions';

/** Read cached permissions for a user from localStorage. */
const readCache = (userId) => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.userId === userId) return parsed.permissions;
  } catch { /* corrupt cache */ }
  return null;
};

/** Write permissions for a user to localStorage. */
const writeCache = (userId, permissions) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ userId, permissions }));
  } catch { /* quota exceeded — ignore */ }
};

/**
 * Provides resolved tab visibility for the current user.
 * Fetches from /api/tab-permissions/my-tabs on login / mount.
 * Caches in localStorage so subsequent loads are instant (no flash).
 *
 * Admin users always see every tab (cannot be locked out).
 */
export const TabPermissionProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState({}); // { tabKey: bool }
  const [loaded, setLoaded] = useState(false);
  const prevUserId = useRef(null);

  // On user change, hydrate from cache immediately (synchronous)
  useEffect(() => {
    if (isAuthenticated && user?.id && user.id !== prevUserId.current) {
      prevUserId.current = user.id;
      const cached = readCache(user.id);
      if (cached) {
        setPermissions(cached);
        setLoaded(true);       // instant — no flash
      }
    }
  }, [isAuthenticated, user]);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await getMyTabs();
      const list = res?.data ?? res ?? [];
      const map = {};
      list.forEach(p => { map[p.tabKey] = p.isVisible; });
      setPermissions(map);
      if (user?.id) writeCache(user.id, map);
    } catch (err) {
      console.error('Failed to fetch tab permissions:', err);
      // On failure, keep cached permissions if available; otherwise default to all visible
      if (!Object.keys(permissions).length) setPermissions({});
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchPermissions();
    } else {
      setPermissions({});
      setLoaded(false);
      prevUserId.current = null;
      try { localStorage.removeItem(CACHE_KEY); } catch {}
    }
  }, [isAuthenticated, user, fetchPermissions]);

  /**
   * Returns true if the tab should be shown.
   * - Admin (role 4) always sees everything.
   * - If no permission data loaded yet, default to visible.
   * - If a tabKey is not in the map, default to visible.
   */
  const isTabVisible = useCallback((tabKey) => {
    // Admin bypass
    if (user?.role === 4) return true;

    // Not loaded yet → don't hide anything
    if (!loaded) return true;

    // Check permission map
    if (tabKey in permissions) return permissions[tabKey];

    // Default visible
    return true;
  }, [user, loaded, permissions]);

  return (
    <TabPermissionContext.Provider value={{ permissions, loaded, isTabVisible, refreshPermissions: fetchPermissions }}>
      {children}
    </TabPermissionContext.Provider>
  );
};

export const useTabPermissions = () => {
  const ctx = useContext(TabPermissionContext);
  if (!ctx) throw new Error('useTabPermissions must be used within TabPermissionProvider');
  return ctx;
};
